/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, no-console */
import { FieldErrors } from 'react-hook-form/dist/types/errors';

declare global {
  interface Window {
    dataLayer: unknown[];
    amplitude: any;
  }
}

export const pageView = (pageSubType: string, pageName: string, clientId?: string) => {
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  dataLayer.push({
    event: 'virtual page view',
    pageType: 'identity',
    pageSubType,
    pageName,
    ...(clientId ? { productType: clientId } : { productType: null }),
  });
};

export const errorPageView = (errorCode: number, reason: string, errorType?: string) => {
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  dataLayer.push({
    event: 'virtual page view',
    pageType: 'error',
    pageSubType: errorCode.toString(),
    pageName: `/ga-virtual/error/${errorCode}`,
    errorType: errorType || 'server',
    errorReason: reason,
  });
};

const parseFieldErrors = (errorFields: FieldErrors) => {
  const sortedErrorFields = Object.keys(errorFields)
    .map((field) => ({
      name: field,
      error: errorFields[field]?.message?.toString() || '',
    }))
    .filter((field) => field.error !== '')
    .sort((a, b) => (a.name > b.name ? 1 : -1));

  return {
    numErrorFields: sortedErrorFields.length,
    errorFields: sortedErrorFields.map(({ name }) => name).join('|'),
    firstErrorField: sortedErrorFields[0]?.name || '',
    firstErrorMessage: sortedErrorFields[0]?.error?.toLowerCase() || '',
  };
};

export const formFieldError = (formName: string, errorFields: FieldErrors) => {
  const dataLayer = window.dataLayer || [];
  const parsedErrorFields = parseFieldErrors(errorFields);
  dataLayer.push({
    event: 'visitor interaction',
    interactionName: 'form error',
    formName: formName.toLowerCase(),
    ...parsedErrorFields,
  });
};

export const failedInteraction = (interactionName: string, error: string) => {
  const dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'visitor interaction',
    interactionName: interactionName.toLowerCase(),
    successFlag: 'error',
    errorType: error.toLowerCase(),
  });
};

export const successfulInteraction = (interactionName: string) => {
  const dataLayer = window.dataLayer || [];

  dataLayer.push({
    event: 'visitor interaction',
    interactionName: interactionName.toLowerCase(),
    successFlag: 'success',
  });
};

export const successfulRegistrationInteraction = (hid: string, clientId: string | undefined) => {
  const dataLayer = window.dataLayer || [];
  const gtmTimeoutMs = 1000;

  dataLayer.push({
    event: 'visitor interaction',
    interactionName: 'register haven identity submit',
    successFlag: 'success',
    authHavenId: hid,
    productType: clientId ?? null,
    eventTimeout: gtmTimeoutMs,
  });
};

export const failedRegistrationInteraction = (message: string) => {
  failedInteraction('register haven identity submit', message);
};

export const successfulLoginInteraction = (
  hid: string,
  redirectUrl: string,
  clientId: string | undefined,
  isOwner: boolean,
) => {
  const gtmTimeoutMs = 1000;
  const flushTimeoutMs = window.amplitude ? 300 : 0;
  const defaultTimeoutMs = gtmTimeoutMs + flushTimeoutMs + 500;
  const dataLayer = window.dataLayer || [];
  const gtmJsIsAccessible = dataLayer.some(
    (item: unknown) => (item as { event?: string })?.event === 'gtm.load',
  );
  // Prevent multiple redirects, if both the eventCallback and the setTimeout fire, it can end up in error.
  // redirectUrl works only for the first call.
  let redirectProcessing = false;

  if (!gtmJsIsAccessible) {
    dataLayer.push({
      event: 'visitor interaction',
      interactionName: 'log in',
      successFlag: 'success',
      authStatus: 'logged in',
      authHavenId: hid,
      productType: clientId ?? null,
      isOwner: isOwner ? 'yes' : 'no',
    });
    window.location.assign(redirectUrl);
    return;
  }

  dataLayer.push({
    event: 'visitor interaction',
    interactionName: 'log in',
    successFlag: 'success',
    authStatus: 'logged in',
    authHavenId: hid,
    productType: clientId ?? null,
    isOwner: isOwner ? 'yes' : 'no',
    eventCallback: () => {
      if (window.amplitude) {
        window.amplitude.flush();
        setTimeout(() => {
          if (redirectProcessing) return;
          redirectProcessing = true;
          window.location.assign(redirectUrl);
        }, flushTimeoutMs);
      } else {
        if (redirectProcessing) return;
        redirectProcessing = true;
        window.location.assign(redirectUrl);
      }
    },
    eventTimeout: gtmTimeoutMs,
  });

  // Fallback in case the eventCallback doesn't fire
  setTimeout(() => {
    if (redirectProcessing) return;
    redirectProcessing = true;
    window.location.assign(redirectUrl);
  }, defaultTimeoutMs);
};

export const clickToCall = (phoneNumber: string) => {
  const trimmedNumber = phoneNumber.replaceAll(' ', '');
  const dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'visitor interaction',
    interactionName: 'click to call',
    phoneNumber: trimmedNumber,
  });
};
